import React from "react";
import { Block, Chip, Label, PassFail, Note, LevelBadge, TextArea } from "./ui";
import { PHASES_TRAINING, RESULT, derive, detail } from "./evalModel";

const SUB_LIST = { "PH-2": ["footwork", "Spot Footwork (Phase 2)"], "PH-3": ["movements", "Body Movement (Phase 3)"] };
const TAG_GROUPS = [
  { key: "Miss Location", title: "Miss Location · finds the limiter", split: (t) => (t.item_id.startsWith("MT-PW") ? "Power" : "Accuracy"), tone: "red" },
  { key: "Ball Flight (context)", title: "Ball Flight · context", split: () => "", tone: "gold" },
];

// Layer 2 · Film / Training: phases (Set -> Spot -> On the Move), then Outcomes · Feel · Mindset.
export function Layer2Film({ L, ev, set }) {
  const l2 = ev.layer2;
  const setL2 = (patch) => set({ layer2: { ...l2, ...patch } });
  const { level, breakdown, allPass } = derive(ev);
  const ph4 = L.itemById["PH-4"];
  const setPhase = (i, patch) => {
    const next = l2.phases.map((p, idx) => (idx === i ? { ...p, ...patch } : p));
    const firstBreak = next.findIndex(p => p.result === RESULT.breaks);
    setL2({ phases: next.map((p, idx) => (firstBreak >= 0 && idx > firstBreak ? { ...p, result: "", footwork: [], movements: [] } : p)) });
  };
  const setSub = (i, key, id, result) => {
    const cur = l2.phases[i][key] || [];
    const list = cur.some(s => s.id === id) ? cur.map(s => (s.id === id ? { ...s, result } : s)) : [...cur, { id, result }];
    setPhase(i, { [key]: list.filter(s => s.result) });
  };
  const toggle = (key, id) => setL2({ [key]: l2[key].includes(id) ? l2[key].filter(x => x !== id) : [...l2[key], id] });
  const areas = L.list("Film / Training Area (Layer 2)");
  const tags = L.list("Miss Tag");

  return (
    <Block kicker="Layer 2 · Film / Training Assessment" title="What do we see when they shoot?" subtitle="Game film · workouts · training · practice" testid="se-layer2"
      right={<LevelBadge L={L} levelId={level} testid="se-level" />}>
      <div className="flex flex-wrap gap-2">{areas.map(a => <span key={a.item_id} className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-white/10 text-[#a0a0a0]" title={a.description}>{a.name}</span>)}</div>

      <div className="space-y-3">
        <Label>Phases · watch them shoot in order · stop at the first that breaks down</Label>
        {PHASES_TRAINING.map((pid, i) => {
          const item = L.itemById[pid];
          const prev = i === 0 ? null : l2.phases[i - 1];
          const locked = i > 0 && prev?.result !== RESULT.pass;
          const row = l2.phases[i];
          const [subKey, subList] = SUB_LIST[pid] || [];
          return (
            <div key={pid} className={`border p-4 ${row.result === RESULT.breaks ? "border-[#f87171]/60 bg-[#f87171]/5" : row.result === RESULT.pass ? "border-[#4ade80]/40" : "border-white/10"} ${locked ? "opacity-40" : ""}`} data-testid={`se-phase-${pid}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575]">Phase {i + 1} · {pid}</div>
                  <div className="font-heading uppercase text-white text-lg leading-tight">{item?.name}</div>
                  <div className="text-xs text-[#a0a0a0] mt-1">{detail(item, "Adds")}</div>
                </div>
                <PassFail value={row.result} onChange={r => setPhase(i, { result: r })} disabled={locked} testid={`se-phase-${pid}-result`} />
              </div>
              {!locked && subKey && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <Label>Optional detail · {subList}</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {L.list(subList).map(s => (
                      <div key={s.item_id} className="flex items-center justify-between gap-2 border border-white/5 px-3 py-2" title={s.details}>
                        <span className="text-xs text-white">{s.name}</span>
                        <PassFail value={(row[subKey] || []).find(x => x.id === s.item_id)?.result || ""} onChange={r => setSub(i, subKey, s.item_id, r)} testid={`se-sub-${s.item_id}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {row.result === RESULT.breaks && <div className="mt-3"><Note testid={`se-focus-${pid}`}><span className="text-[#f87171] font-bold uppercase tracking-[0.2em] text-[9px] mr-2">If it breaks down here</span>{detail(item, "If it breaks down here")}</Note></div>}
            </div>
          );
        })}
        {allPass && <div className="border border-[var(--org-accent)]/50 bg-[var(--org-accent)]/5 p-4 text-sm text-white" data-testid="se-ph4-focus">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)] font-bold mb-1">Good in training, low game % · Phase 4</div>
          Add live defenders: primary read, find space, get it off.<div className="text-xs text-[#a0a0a0] mt-1">{detail(ph4, "If it breaks down here")}</div>
        </div>}
        {breakdown && <Note testid="se-train-phase">Breaks down in training → train in <span className="text-white font-bold">{L.itemById[breakdown]?.name}</span> with its focus.</Note>}
      </div>

      <div className="border-t border-white/10 pt-4" data-testid="se-outcomes">
        <Label>Outcomes · makes and misses come from Layer 1</Label>
        {TAG_GROUPS.map(g => { const items = tags.filter(t => t.group === g.key); const subs = [...new Set(items.map(g.split))]; return (
          <div key={g.key} className="mb-3">
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] mb-1.5">{g.title}</div>
            <div className="space-y-1.5">
              {subs.map(s => (
                <div key={s} className="flex flex-wrap items-center gap-1.5">
                  {s && <span className="text-[9px] uppercase tracking-[0.3em] text-[#757575] w-20 shrink-0">{s}</span>}
                  {items.filter(t => g.split(t) === s).map(t => <Chip key={t.item_id} tone={g.tone} active={l2.miss_tag_ids.includes(t.item_id)} onClick={() => toggle("miss_tag_ids", t.item_id)} testid={`se-tag-${t.item_id}`}>{t.name}</Chip>)}
                </div>
              ))}
            </div>
          </div>); })}
        <Note testid="se-layer2-note">Most players miss short, so it's usually a power generation issue. A limiter is a pattern that keeps showing up, not a one-off miss.</Note>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-t border-white/10 pt-4">
        <div data-testid="se-feel">
          <Label>Feel · rhythm & timing · touch · adjusting after a miss</Label>
          <TextArea value={l2.feel_notes} onChange={v => setL2({ feel_notes: v })} testid="se-feel-notes" rows={4} placeholder="No mechanics talk — what does the shot feel like to watch?" />
        </div>
        <div data-testid="se-mindset">
          <Label>Mindset · mark any that apply</Label>
          <div className="flex flex-wrap gap-1.5 mb-2">{L.list("Mindset (Layer 2)").map(m => <Chip key={m.item_id} active={l2.mindset_ids.includes(m.item_id)} onClick={() => toggle("mindset_ids", m.item_id)} testid={`se-ms-${m.item_id}`} title={m.description}>{m.name}</Chip>)}</div>
          <TextArea value={l2.mindset_notes} onChange={v => setL2({ mindset_notes: v })} testid="se-mindset-notes" rows={2} placeholder="How do they shoot after misses, under pressure, when it counts?" />
          {L.itemById["TA-03"]?.linked && <div className="text-[10px] text-[#575757] mt-1">Character DNA: {L.itemById["TA-03"].linked}</div>}
        </div>
      </div>
    </Block>
  );
}
